/**
 * 图纸管理 - 素材库页面
 * 左栏：部件类型列表（系统预设，只读）；「模板配置」权限者可管理文档模板
 * 右栏：当前类型的素材列表（自由参数编辑）
 * 两个入口：「图纸导入」= 上传图纸 → AI 识别参数 → 确认后随素材保存；
 *          「新增素材」= 手动填写信息与自定义参数
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Pencil, Trash2, Loader2, X,
  Upload, Download, Layers, FileText, Eye, Sparkles,
} from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import ParamEditor from '../components/ParamEditor';
import type {
  DrawingComponentType, DrawingParamItem,
  DrawingMaterialListItem, DrawingMaterial, DrawingDocTemplate, DrawingFileItem,
} from '../types/api';
import DrawingUploader, { DRAWING_EXTS } from '../components/DrawingUploader';
import styles from './DrawingMaterials.module.css';

const PREVIEWABLE = ['pdf', 'jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff'];
const formatSize = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : b < 1073741824 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1073741824).toFixed(2)} GB`);
// AI 识别支持的格式（PDF/图片）；其余格式引导手动录入
const AI_EXTRACT_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'webp'];

export default function DrawingMaterialsPage() {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const { hasPermission } = useAuth();
  const canManageTemplate = hasPermission('drawing:模板配置'); // 仅控制文档模板管理

  const [types, setTypes] = useState<DrawingComponentType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // 素材列表
  const [materials, setMaterials] = useState<DrawingMaterialListItem[]>([]);
  const [materialsTotal, setMaterialsTotal] = useState(0);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialsLoading, setMaterialsLoading] = useState(false);

  // 素材编辑弹窗
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<DrawingMaterial | null>(null);
  const [materialForm, setMaterialForm] = useState({
    material_name: '', specification: '', remark: '',
    params: [] as DrawingParamItem[],
    params_source: 'manual' as 'manual' | 'ai',
  });
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [materialDrawings, setMaterialDrawings] = useState<DrawingFileItem[]>([]);
  const [viewMode, setViewMode] = useState(false);
  const [pendingDrawings, setPendingDrawings] = useState<File[]>([]); // 新增时暂存的图纸（保存后自动上传）
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // 图纸导入弹窗（单表单：文件 + 识别 + 素材信息，一体化）
  const [showDrawingImport, setShowDrawingImport] = useState(false);
  const [diFile, setDiFile] = useState<File | null>(null);
  const [diRecognizing, setDiRecognizing] = useState(false);
  const [diForm, setDiForm] = useState({
    material_name: '', remark: '',
    params: [] as DrawingParamItem[],
    params_source: 'manual' as 'manual' | 'ai',
  });
  const [diSaving, setDiSaving] = useState(false);
  const [diStatus, setDiStatus] = useState<string | null>(null);
  const [editRecognizing, setEditRecognizing] = useState(false); // 编辑弹窗内快捷识别中

  // 文档模板弹窗
  const [showTplModal, setShowTplModal] = useState(false);
  const [templates, setTemplates] = useState<DrawingDocTemplate[]>([]);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getDrawingComponentTypes();
      // 只显示启用的预设类型（遗留停用类型不在导航中展示）
      const enabled = (res.data || []).filter(t => t.is_enabled);
      setTypes(enabled);
      if (enabled.length > 0) {
        setSelectedTypeId(prev => (prev && enabled.some(t => t.component_type_id === prev) ? prev : enabled[0].component_type_id));
      } else {
        setSelectedTypeId(null);
      }
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  }, [showToast]);

  const loadMaterials = useCallback(async () => {
    if (!selectedTypeId) { setMaterials([]); setMaterialsTotal(0); return; }
    setMaterialsLoading(true);
    try {
      const res = await api.getDrawingMaterials({ component_type_id: selectedTypeId, keyword: materialSearch || undefined, page: 1, page_size: 100 });
      setMaterials(res.data || []);
      setMaterialsTotal(res.total || 0);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setMaterialsLoading(false); }
  }, [selectedTypeId, materialSearch, showToast]);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  // ==================== 素材 CRUD ====================

  const openMaterialModal = async (material?: DrawingMaterialListItem, view = false) => {
    if (!selectedTypeId) return;
    setViewMode(view);
    setPendingDrawings([]);
    setSaveStatus(null);
    try {
      if (material) {
        const res = await api.getDrawingMaterial(material.material_id);
        const d = res.data!;
        setEditingMaterial(d);
        setMaterialForm({
          material_name: d.material_name,
          specification: d.specification || '', remark: d.remark || '',
          params: (d.params || []).map(p => ({ name: p.name, value: p.value ?? '', unit: p.unit || '' })),
          params_source: d.params_source === 'ai' ? 'ai' : 'manual',
        });
        // 加载素材关联图纸
        const drRes = await api.getDrawingMaterialDrawings(material.material_id);
        setMaterialDrawings(drRes.data || []);
      } else {
        setEditingMaterial(null);
        setMaterialDrawings([]);
        setMaterialForm({ material_name: '', specification: '', remark: '', params: [], params_source: 'manual' });
      }
      setShowMaterialModal(true);
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleViewMaterial = (m: DrawingMaterialListItem) => openMaterialModal(m, true);

  // 删除素材关联图纸
  const handleDeleteMaterialDrawing = async (d: DrawingFileItem) => {
    const ok = await confirmDialog.confirm({ title: '确认删除', message: `确定删除图纸「${d.file_name}」？`, confirmText: '删除', danger: true });
    if (!ok) return;
    try {
      await api.deleteDrawingFile(d.drawing_id);
      showToast('success', '已删除');
      setMaterialDrawings(prev => prev.filter(x => x.drawing_id !== d.drawing_id));
      loadMaterials(); loadTypes();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleSaveMaterial = async () => {
    if (!materialForm.material_name.trim()) { showToast('warning', '请输入素材名称'); return; }
    const cleanedParams = cleanParams(materialForm.params);
    const emptyName = cleanedParams.some(p => !p.name);
    if (emptyName) { showToast('warning', '存在未填写参数名的参数行，请补全或删除'); return; }
    setSavingMaterial(true);
    try {
      if (editingMaterial) {
        await api.updateDrawingMaterial(editingMaterial.material_id, {
          material_name: materialForm.material_name,
          specification: materialForm.specification || undefined,
          remark: materialForm.remark || undefined,
          params: cleanedParams,
          params_source: materialForm.params_source,
        });
        showToast('success', '更新成功');
        setShowMaterialModal(false);
        loadMaterials(); loadTypes();
      } else {
        // 新增：先创建素材（编码自动生成），再自动上传暂存的图纸（可选）
        const res = await api.createDrawingMaterial({
          component_type_id: selectedTypeId!,
          material_name: materialForm.material_name,
          specification: materialForm.specification || undefined,
          remark: materialForm.remark || undefined,
          params: cleanedParams,
          params_source: materialForm.params_source,
        });
        const created = res.data!;
        for (let i = 0; i < pendingDrawings.length; i++) {
          setSaveStatus(`上传图纸 ${i + 1}/${pendingDrawings.length}`);
          await api.uploadDrawingFile(pendingDrawings[i], { scope: 'material', material_id: created.material_id });
        }
        setPendingDrawings([]);
        showToast('success', pendingDrawings.length > 0 ? '创建成功，图纸已上传' : '创建成功');
        setShowMaterialModal(false);
        loadMaterials(); loadTypes();
      }
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setSavingMaterial(false); }
  };

  // ==================== 图纸导入（单表单一体化） ====================

  const openDrawingImport = () => {
    setDiFile(null);
    setDiStatus(null);
    setDiRecognizing(false);
    setDiForm({ material_name: '', remark: '', params: [], params_source: 'manual' });
    setShowDrawingImport(true);
  };

  const closeDrawingImport = () => {
    setShowDrawingImport(false);
    setDiStatus(null);
  };

  const cleanParams = (params: DrawingParamItem[]) =>
    params
      .map(p => ({ name: (p.name || '').trim(), value: (p.value ?? '').trim(), unit: (p.unit || '').trim() }))
      .filter(p => p.name || p.value);

  // 点击识别：调视觉大模型，结果直接填入表单（素材名称 + 参数），全部可再修改
  const handleDiRecognize = async () => {
    if (!diFile) { showToast('warning', '请先选择图纸文件'); return; }
    setDiRecognizing(true);
    try {
      const res = await api.extractDrawingParams(diFile);
      const data = res.data!;
      const params = (data.params || []).map(p => ({ name: p.name, value: p.value ?? '', unit: p.unit || '' }));
      const name = (data.material_name || '').trim();
      setDiForm(prev => ({
        ...prev,
        material_name: name || prev.material_name,
        params,
        params_source: params.length > 0 ? 'ai' : prev.params_source,
      }));
      const parts = [params.length > 0 ? `${params.length} 项参数` : '未识别到参数', name ? `名称「${name}」` : ''];
      showToast(params.length > 0 ? 'success' : 'warning', `识别完成：${parts.filter(Boolean).join('，已填入表单，') || ''}请核对修改`);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setDiRecognizing(false); }
  };

  // 保存：创建素材（参数 JSON 落库）→ 上传图纸附件
  const handleDiSave = async () => {
    if (!diForm.material_name.trim()) { showToast('warning', '请输入素材名称'); return; }
    const params = cleanParams(diForm.params);
    if (params.some(p => !p.name)) { showToast('warning', '存在未填写参数名的参数行，请补全或删除'); return; }
    setDiSaving(true);
    try {
      const res = await api.createDrawingMaterial({
        component_type_id: selectedTypeId!,
        material_name: diForm.material_name,
        remark: diForm.remark || undefined,
        params,
        params_source: diForm.params_source,
      });
      const created = res.data!;
      if (diFile) {
        setDiStatus('上传图纸附件...');
        await api.uploadDrawingFile(diFile, { scope: 'material', material_id: created.material_id });
      }
      showToast('success', diFile ? '图纸导入成功（素材与图纸已保存）' : '素材创建成功');
      setShowDrawingImport(false);
      setDiStatus(null);
      loadMaterials(); loadTypes();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setDiSaving(false); }
  };

  // 编辑弹窗内快捷识别：识别结果经确认后覆盖当前参数
  const handleEditRecognize = async (file: File) => {
    setEditRecognizing(true);
    try {
      const res = await api.extractDrawingParams(file);
      const params = (res.data?.params || []).map(p => ({ name: p.name, value: p.value ?? '', unit: p.unit || '' }));
      if (params.length === 0) { showToast('warning', '未识别到参数'); return; }
      const ok = await confirmDialog.confirm({
        title: '应用识别结果',
        message: `AI 识别到 ${params.length} 项参数，将覆盖当前参数列表。是否应用？`,
        confirmText: '应用',
      });
      if (!ok) return;
      setMaterialForm(prev => ({ ...prev, params, params_source: 'ai' }));
      showToast('success', '识别结果已填入参数列表，可继续修改');
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setEditRecognizing(false); }
  };

  const handleDeleteMaterial = async (m: DrawingMaterialListItem) => {
    const ok = await confirmDialog.confirm({ title: '确认删除', message: `确定删除素材「${m.material_name}」？`, confirmText: '删除', danger: true });
    if (!ok) return;
    try {
      await api.deleteDrawingMaterial(m.material_id);
      showToast('success', '已删除'); loadMaterials(); loadTypes();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  // ==================== 文档模板 ====================

  const openTplModal = async () => {
    try {
      const res = await api.getDrawingDocTemplates();
      setTemplates(res.data || []);
      setShowTplModal(true);
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleUploadTpl = async (docType: 'param_card' | 'manual', file: File) => {
    try {
      await api.uploadDrawingDocTemplate(docType, file);
      showToast('success', '模板已上传并启用');
      const res = await api.getDrawingDocTemplates();
      setTemplates(res.data || []);
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const selectedType = types.find(t => t.component_type_id === selectedTypeId);

  return (
    <div className={styles.container}>
      {/* ===== 页头 ===== */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>素材库</h1>
          <p className={styles.subtitle}>维护部件素材，供产品组合与文档生成使用（图纸导入自动识别参数，新增素材自定义参数）</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary} onClick={openDrawingImport} disabled={!selectedTypeId}>
            <Sparkles size={15} /> 图纸导入
          </button>
          <button className={styles.btnPrimary} onClick={() => openMaterialModal()} disabled={!selectedTypeId}>
            <Plus size={15} /> 新增素材
          </button>
        </div>
      </div>

      <div className={styles.body}>
        {/* ===== 左栏：部件类型（系统预设，只读） ===== */}
        <div className={styles.typePanel}>
          <div className={styles.typePanelHeader}>
            <span><Layers size={15} /> 部件类型</span>
            {canManageTemplate && (
              <div className={styles.typePanelActions}>
                <button className={styles.iconBtn} title="文档模板管理" onClick={openTplModal}><FileText size={15} /></button>
              </div>
            )}
          </div>
          {loading ? (
            <div className={styles.loading}><Loader2 size={22} className={styles.spin} /></div>
          ) : types.length === 0 ? (
            <div className={styles.empty}>
              <p>暂无部件类型</p>
              <p className={styles.emptyHint}>部件类型由系统预设，请检查后端预设配置</p>
            </div>
          ) : (
            <div className={styles.typeList}>
              {types.map(t => (
                <div
                  key={t.component_type_id}
                  className={`${styles.typeItem} ${t.component_type_id === selectedTypeId ? styles.typeItemActive : ''} ${!t.is_enabled ? styles.typeItemDisabled : ''}`}
                  onClick={() => t.is_enabled && setSelectedTypeId(t.component_type_id)}
                >
                  <div className={styles.typeItemMain}>
                    <span className={styles.typeItemName}>{t.type_name}</span>
                    <span className={styles.typeItemMeta}>{t.material_count} 素材</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ===== 右栏：素材列表 ===== */}
        <div className={styles.materialPanel}>
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input placeholder={`搜索${selectedType?.type_name || ''}素材名称/编码...`} value={materialSearch} onChange={e => setMaterialSearch(e.target.value)} />
            </div>
            <span className={styles.countText}>共 {materialsTotal} 条</span>
          </div>
          <div className={styles.tableWrap}>
            {materialsLoading ? (
              <div className={styles.loading}><Loader2 size={22} className={styles.spin} /></div>
            ) : materials.length === 0 ? (
              <div className={styles.empty}>
                <p>{selectedTypeId ? '暂无素材' : '请先在左侧选择部件类型'}</p>
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>素材名称</th>
                    <th style={{ width: '14%' }}>素材编码</th>
                    <th style={{ width: '17%' }}>规格</th>
                    <th style={{ width: '10%' }}>图纸</th>
                    <th style={{ width: '12%' }}>创建人</th>
                    <th style={{ width: '16%' }}>更新时间</th>
                    <th style={{ width: '11%' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map(m => (
                    <tr key={m.material_id}>
                      <td className={styles.cellStrong}>{m.material_name}</td>
                      <td>{m.material_code}</td>
                      <td className={styles.cellEllipsis}>{m.specification || '-'}</td>
                      <td>{m.drawing_count} 份</td>
                      <td>{m.created_by_name || '-'}</td>
                      <td>{m.update_time ? new Date(m.update_time).toLocaleDateString('zh-CN') : '-'}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.iconBtn} title="查看" onClick={() => handleViewMaterial(m)}><Eye size={14} /></button>
                          <button className={styles.iconBtn} title="编辑" onClick={() => openMaterialModal(m)}><Pencil size={14} /></button>
                          <button className={styles.iconBtn} title="删除" onClick={() => handleDeleteMaterial(m)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ===== 弹窗：素材编辑（动态参数表单，字段来自系统预设模板） ===== */}
      {showMaterialModal && (
        <div className={styles.modalOverlay} onClick={() => setShowMaterialModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{viewMode ? `查看素材${selectedType ? ` — ${selectedType.type_name}` : ''}` : editingMaterial ? '编辑素材' : `新增素材${selectedType ? ` — ${selectedType.type_name}` : ''}`}</h2>
              <button className={styles.modalClose} onClick={() => { setShowMaterialModal(false); setViewMode(false); }}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label>素材名称 <span className={styles.required}>*</span></label>
                  <input value={materialForm.material_name} disabled={viewMode} onChange={e => setMaterialForm(p => ({ ...p, material_name: e.target.value }))} placeholder="如：罐身-401" />
                </div>
                {editingMaterial && (
                  <div className={styles.formField}>
                    <label>素材编码<span className={styles.hint}>（系统自动生成，不可修改）</span></label>
                    <input value={editingMaterial.material_code} disabled />
                  </div>
                )}
                <div className={styles.formField}>
                  <label>规格</label>
                  <input value={materialForm.specification} disabled={viewMode} onChange={e => setMaterialForm(p => ({ ...p, specification: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label>备注</label>
                  <input value={materialForm.remark} disabled={viewMode} onChange={e => setMaterialForm(p => ({ ...p, remark: e.target.value }))} />
                </div>
              </div>

              <div className={styles.sectionTitle}>
                参数（自由参数，可增删改）
                {!viewMode && (
                  <label className={styles.btnSecondary} style={{ marginLeft: 10, padding: '4px 10px', cursor: editRecognizing ? 'wait' : 'pointer' }}>
                    <Sparkles size={13} /> {editRecognizing ? '识别中...' : 'AI 识别'}
                    <input
                      type="file"
                      accept={AI_EXTRACT_EXTS.map(e => '.' + e).join(',')}
                      style={{ display: 'none' }}
                      disabled={editRecognizing}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleEditRecognize(f); e.target.value = ''; }}
                    />
                  </label>
                )}
                {materialForm.params_source === 'ai' && <span className={styles.aiSourceTag}>来自 AI 识别</span>}
              </div>
              <ParamEditor
                value={materialForm.params}
                readOnly={viewMode}
                onChange={params => setMaterialForm(p => ({ ...p, params }))}
              />
              <div className={styles.sectionTitle}>关联图纸{!editingMaterial && !viewMode && <span className={styles.hint}>（可选，保存素材后自动上传）</span>}</div>
              {editingMaterial ? (
                <>
                  {materialDrawings.length === 0 && <div className={styles.noFields}>暂无关联图纸</div>}
                  {materialDrawings.map(d => (
                    <div key={d.drawing_id} className={styles.fileRow}>
                      <FileText size={14} />
                      <span className={styles.fileName}>{d.file_name}</span>
                      <span className={styles.fileMeta}>{formatSize(d.file_size)}</span>
                      <div className={styles.rowActions}>
                        {d.url && PREVIEWABLE.includes(d.file_ext) && (
                          <button className={styles.iconBtn} title="预览" onClick={() => window.open(d.url!, '_blank')}><Eye size={13} /></button>
                        )}
                        {d.url && <button className={styles.iconBtn} title="下载" onClick={() => window.open(d.url!, '_blank')}><Download size={13} /></button>}
                        {!viewMode && (
                          <button className={styles.iconBtn} title="删除" onClick={() => handleDeleteMaterialDrawing(d)}><Trash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                  {!viewMode && (
                    <DrawingUploader
                      scope="material"
                      materialId={editingMaterial.material_id}
                      onUploaded={d => { setMaterialDrawings(prev => [d, ...prev]); loadMaterials(); loadTypes(); }}
                    />
                  )}
                </>
              ) : (
                <>
                  {pendingDrawings.length === 0 && (
                    <div className={styles.noFields}>尚未选择图纸。可暂不上传，或先添加文件，保存素材时自动上传</div>
                  )}
                  {pendingDrawings.map((f, i) => (
                    <div key={`${f.name}-${i}`} className={styles.fileRow}>
                      <FileText size={14} />
                      <span className={styles.fileName}>{f.name}</span>
                      <span className={styles.fileMeta}>{formatSize(f.size)}</span>
                      <div className={styles.rowActions}>
                        <button className={styles.iconBtn} title="移除" onClick={() => setPendingDrawings(prev => prev.filter((_, j) => j !== i))}><X size={13} /></button>
                      </div>
                    </div>
                  ))}
                  <label className={styles.filePick}>
                    <Upload size={13} /> 添加图纸文件
                    <input
                      type="file"
                      multiple
                      accept={DRAWING_EXTS.map(e => '.' + e).join(',')}
                      style={{ display: 'none' }}
                      onChange={e => {
                        const skipped: string[] = [];
                        const valid = Array.from(e.target.files || []).filter(f => {
                          if (f.size === 0) { skipped.push(`「${f.name}」空文件`); return false; }
                          if (f.size > 500 * 1024 * 1024) { skipped.push(`「${f.name}」超 500MB`); return false; }
                          return true;
                        });
                        if (skipped.length) showToast('error', `已跳过：${skipped.join('、')}`);
                        if (valid.length) setPendingDrawings(prev => [...prev, ...valid]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => { setShowMaterialModal(false); setViewMode(false); }}>
                {viewMode ? '关闭' : '取消'}
              </button>
              {!viewMode && (
                <button className={styles.btnPrimary} disabled={savingMaterial} onClick={handleSaveMaterial}>
                  {savingMaterial
                    ? <><Loader2 size={14} className={styles.spin} /> {saveStatus || '保存中...'}</>
                    : pendingDrawings.length > 0 ? `保存并上传（${pendingDrawings.length} 张图纸）` : '保存'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== 弹窗：图纸导入（文件上传 + AI 识别 + 表单确认，一体化） ===== */}
      {showDrawingImport && (
        <div className={styles.modalOverlay} onClick={closeDrawingImport}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>图纸导入{selectedType ? ` — ${selectedType.type_name}` : ''}</h2>
              <button className={styles.modalClose} onClick={closeDrawingImport}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              {/* 步骤1：上传图纸 + 识别 */}
              <div className={styles.sectionTitle} style={{ marginTop: 0 }}>1. 上传图纸并 AI 识别</div>
              <div className={styles.importStep}>
                <label className={styles.filePick}>
                  <Upload size={14} /> {diFile ? diFile.name : '选择图纸文件（PDF / 图片）'}
                  <input
                    type="file"
                    accept={AI_EXTRACT_EXTS.map(e => '.' + e).join(',')}
                    style={{ display: 'none' }}
                    disabled={diRecognizing || diSaving}
                    onChange={e => { const f = e.target.files?.[0]; if (f) setDiFile(f); e.target.value = ''; }}
                  />
                </label>
                <button
                  className={styles.btnPrimary}
                  disabled={!diFile || diRecognizing || diSaving}
                  onClick={handleDiRecognize}
                >
                  {diRecognizing
                    ? <><Loader2 size={14} className={styles.spin} /> 识别中（约需十几秒）...</>
                    : <><Sparkles size={14} /> 开始识别</>}
                </button>
                {diFile && !diRecognizing && (
                  <button className={styles.iconBtn} title="移除文件" onClick={() => setDiFile(null)}><X size={14} /></button>
                )}
              </div>
              <p className={styles.hintBar}>
                识别结果将自动填入下方表单，素材名称、备注、参数均可修改（支持 CAD 转 PDF；无图纸也可直接填写表单）。
              </p>

              {/* 步骤2：确认表单 */}
              <div className={styles.sectionTitle}>2. 确认素材信息</div>
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label>素材名称 <span className={styles.required}>*</span></label>
                  <input
                    value={diForm.material_name}
                    placeholder="识别后自动填入，可修改；如：罐身-401"
                    disabled={diSaving}
                    onChange={e => setDiForm(p => ({ ...p, material_name: e.target.value }))}
                  />
                </div>
                <div className={styles.formField}>
                  <label>素材备注</label>
                  <input
                    value={diForm.remark}
                    placeholder="选填"
                    disabled={diSaving}
                    onChange={e => setDiForm(p => ({ ...p, remark: e.target.value }))}
                  />
                </div>
              </div>

              <div className={styles.sectionTitle}>
                参数
                {diForm.params_source === 'ai' && <span className={styles.aiSourceTag}>来自 AI 识别</span>}
              </div>
              {diForm.params.some(p => !p.name.trim() && p.value) && (
                <p className={styles.nameHintBar}>
                  图纸中有 {diForm.params.filter(p => !p.name.trim() && p.value).length} 项参数未标注名称（橙色行），
                  请根据图纸含义补充参数名后保存。
                </p>
              )}
              <ParamEditor
                value={diForm.params}
                onChange={params => setDiForm(p => ({ ...p, params }))}
                maxRows={100}
              />

              <div className={styles.sectionTitle}>附件</div>
              {diFile ? (
                <div className={styles.fileRow}>
                  <FileText size={14} />
                  <span className={styles.fileName}>{diFile.name}</span>
                  <span className={styles.fileMeta}>{formatSize(diFile.size)}</span>
                  <div className={styles.rowActions}>
                    <button className={styles.iconBtn} title="移除" disabled={diSaving} onClick={() => setDiFile(null)}><X size={13} /></button>
                  </div>
                </div>
              ) : (
                <div className={styles.noFields}>尚未选择图纸文件（可选，保存素材后也可在编辑中补充上传）</div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={closeDrawingImport}>取消</button>
              <button className={styles.btnPrimary} disabled={diSaving} onClick={handleDiSave}>
                {diSaving
                  ? <><Loader2 size={14} className={styles.spin} /> {diStatus || '保存中...'}</>
                  : diFile ? '保存素材并上传图纸' : '保存素材'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 弹窗：文档模板管理 ===== */}
      {showTplModal && (
        <div className={styles.modalOverlay} onClick={() => setShowTplModal(false)}>
          <div className={`${styles.modal} ${styles.modalSmall}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>文档模板管理</h2>
              <button className={styles.modalClose} onClick={() => setShowTplModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.hintBar}>
                上传含占位符的 Word 模板（.docx）替换默认模板；生成文档时按当前模板填充数据。
              </p>
              {(['param_card', 'manual'] as const).map(docType => {
                const list = templates.filter(t => t.doc_type === docType);
                const current = list.find(t => t.is_current);
                return (
                  <div key={docType} className={styles.tplSection}>
                    <div className={styles.tplTitle}>{docType === 'param_card' ? '参数卡模板' : '说明书模板'}</div>
                    <div className={styles.tplCurrent}>
                      当前：{current ? `${current.file_name}（v${current.version}）` : '默认模板（首次生成时自动创建）'}
                    </div>
                    <div className={styles.tplActions}>
                      <button className={styles.btnSecondary} onClick={() => api.downloadCurrentDrawingTemplate(docType)}>
                        <Download size={13} /> 下载当前
                      </button>
                      <label className={styles.filePick}>
                        <Upload size={13} /> 上传新模板
                        <input type="file" accept=".docx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadTpl(docType, f); }} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
